import { useEffect, useState } from 'react';

import { RiBookmarkFill, RiBookmarkLine } from '@remixicon/react';

import { ButtonIcon } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { useFavoritesCollection } from '../hooks';
import { useFavorites } from './FavoritesProvider';

export const FavoriteIconButton = ({
  resourceName,
  resourceLabel,
}: {
  resourceName: string;
  resourceLabel: string;
}) => {
  const { entryNamesByResource, loading } = useFavorites();
  const { addToFavorites, removeFromFavorites } = useFavoritesCollection();
  const [pending, setPending] = useState<boolean | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const entryNames = entryNamesByResource.get(resourceName) ?? [];
  const serverState = entryNames.length > 0;
  const isFavorited = pending ?? serverState;

  useEffect(() => {
    if (pending === serverState) setPending(null);
  }, [pending, serverState]);
  const label = isFavorited
    ? `Remove ${resourceLabel} from favorites`
    : `Add ${resourceLabel} to favorites`;
  const Icon = isFavorited ? RiBookmarkFill : RiBookmarkLine;

  const toggle = async () => {
    setPending(!isFavorited);
    setInFlight(true);
    try {
      if (isFavorited) {
        await Promise.all(entryNames.map(name => removeFromFavorites(name)));
      } else {
        await addToFavorites(resourceName);
      }
    } catch {
      setPending(null);
    } finally {
      setInFlight(false);
    }
  };

  return (
    <TooltipRoot>
      <ButtonIcon
        variant="tertiary"
        size="small"
        aria-label={label}
        aria-pressed={isFavorited}
        disabled={loading || inFlight}
        onClick={event => {
          event.stopPropagation();
          void toggle();
        }}
        data-track-id="button.favorite"
        data-track-action={isFavorited ? 'delete' : 'create'}
       data-track-source-anchor="3604b6ec0b;favorite-icon-button-icon;low">
        <Icon size={16} aria-hidden />
      </ButtonIcon>
      <Tooltip>{label}</Tooltip>
    </TooltipRoot>
  );
};
